import { normalizeUsers } from '@hr/common/utils'
import i18n from '../i18n'
import {
	getFile,
	serverDelete,
	serverGet,
	serverGetPublic,
	serverPatch,
	serverPost,
	serverPostPublic,
	serverPut,
} from './client/backendclient'
import { localizationServerGet, localizationServerPost } from './client/localizationclient'
import { pdfReportServerPost } from './client/reportclient'
import {
	storageServerDelete,
	storageServerGet,
	storageServerGetFile,
	storageServerPatch,
	storageServerPost,
	storageServerPut,
} from './client/storageclient'

export const oneDayMs = 1000 * 60 * 60 * 24

export const loginMessageQuery = {
	queryKey: ['loginMessage'],
	queryFn: async () => (await serverGetPublic('/login/message')).data,
	staleTime: oneDayMs,
}

export const languageCodesGet = () => serverGet('/api/v1/rest/languagecodes')
export const countryCodesGet = () => serverGet('/api/v1/rest/countrycodes')
export const icdCodesQuery = {
	queryKey: ['icdCodes'],
	queryFn: () => serverGet('/api/v1/rest/icd-codes').then((res) => res.data.split('\n').map((value) => ({ value }))), // create array from newline separated string
	staleTime: oneDayMs,
}
export const dashboardGet = () => serverGet('/api/v1/rest/dashboard') // namedays
export const personalDashboardGet = () => serverGet('/api/v1/rest/user-portal/dashboard') // personal data
export const personalSupervisorsGet = () => serverGet('/api/v1/rest/user-portal/supervisors') // supervisors

export const chartAnniversaryGet = () => serverGet('/api/v1/rest/charts/anniversary')
export const chartAnniversaryQuery = {
	queryKey: ['chartAnniversary'],
	queryFn: chartAnniversaryGet,
	staleTime: oneDayMs,
}

export const eSalaryGet = (endpoint, headers) => serverGet(`/api/v1/rest/esalary/endpoint/${endpoint}`, null, headers) // pass UI language as header
export const salaryAbsencesQuery = {
	queryKey: ['salaryAbsences'],
	queryFn: async () => (await eSalaryGet('salaryabsences')).data,
	staleTime: oneDayMs,
}
export const vacancyQuery = {
	queryKey: ['vacancy'],
	queryFn: async () => (await eSalaryGet('vacancy')).data,
	staleTime: oneDayMs,
}

export const getProcountorEmployeesExpanded = (expand) =>
	serverGet(`/api/v1/rest/procountor/employees?expand=${expand}`)
export const procountorEmployeeExpandedQuery = {
	queryKey: ['procountorEmployeeExpanded'],
	queryFn: async () => (await getProcountorEmployeesExpanded('holidayPay')).data,
	staleTime: oneDayMs,
}
export const procountorWorkingHourReductionQuery = {
	queryKey: ['workingHourReduction'],
	queryFn: async () => (await getProcountorEmployeesExpanded('workingHourReduction')).data,
	staleTime: oneDayMs,
}

export const getProcountorAbsences = (language) => serverGet('/api/v1/rest/procountor/absences', { language: language })
export const procountorAbsencesQuery = (language) => ({
	queryKey: ['procountorAbsences', language],
	queryFn: async () => (await getProcountorAbsences(language)).data,
	staleTime: oneDayMs,
})

export const getProcountorWorkHoursAndOvertimes = (language) =>
	serverGet('/api/v1/rest/procountor/work-hours-and-overtimes', {
		language: language,
	})
export const procountorWorkHoursAndOvertimesQuery = (language) => ({
	queryKey: ['workHoursAndOvertimes', language],
	queryFn: async () => (await getProcountorWorkHoursAndOvertimes(language)).data,
	staleTime: oneDayMs,
})

export const getProcountorIntegrityCheck = (scopes) => {
	let url = '/api/v1/rest/procountor/integrity-check'

	if (Array.isArray(scopes) && scopes.length > 0) {
		const params = new URLSearchParams()
		scopes.forEach((scope) => {
			params.append('scope', scope)
		})
		url += `?${params.toString()}`
	}
	return serverGet(url, null)
}
export const procountorIntegrityCheckQuery = {
	queryKey: ['procountorIntegrityCheck'],
	queryFn: async () => (await getProcountorIntegrityCheck()).data,
	staleTime: oneDayMs,
}

export const getProcountorSalaryMappingNames = () => serverGet('/api/v1/rest/salary-mapping/supplements/names')
export const procountorSalaryMappingNamesQuery = (enabled = true) => ({
	queryKey: ['procountorSalaryMappingNames'],
	queryFn: async () => (await getProcountorSalaryMappingNames()).data,
	staleTime: oneDayMs,
	enabled: !!enabled,
})

export const procountorInitializeBenefitMappings = () =>
	serverPost(`/api/v1/rest/salary-mapping/supplements/initialize`)

export const initializeCalendarEventMappings = () => serverPost(`/api/v1/rest/calendar/mappings/initialize`)

export const timeTrackingAbsencesSync = () => serverPost(`/api/v1/rest/calendar/sync/time-tracking/absences`)

export const timeTrackingWorkHoursAndOvertimesSync = () =>
	serverPost(`/api/v1/rest/calendar/sync/time-tracking/work-hours-and-overtimes`)

//Time entries for statistics TYPES = ABSENCES & WORK_HOURS_AND_OVERTIMES
export const timeTrackingEntriesGet = (type, startTime, endTime) => {
	const params = new URLSearchParams({
		type,
	})

	if (startTime) {
		params.append('startTime', startTime)
	}

	if (endTime) {
		params.append('endTime', endTime)
	}

	return serverGet(`/api/v1/rest/calendar/time-tracking/entries?${params.toString()}`)
}

export const timeTrackingEntriesQuery = (type, startTime, endTime) => ({
	queryKey: ['timeTrackingEntries', type, startTime, endTime],
	queryFn: async () => (await timeTrackingEntriesGet(type, startTime, endTime)).data,
})

export const finagoTimeEventTypesGet = () => serverGet('/api/v1/rest/esalary/finago-time/event-types')
export const finagoTimeEventTypesQuery = {
	queryKey: ['finagoTimeEventTypes'],
	queryFn: async () => (await finagoTimeEventTypesGet()).data,
	staleTime: 5 * 60 * 1000,
}

export const finagoTimeEventMappingsGet = () => serverGet('/api/v1/rest/calendar/time-tracking/mappings')

export const finagoTimeEventMappingsQuery = {
	queryKey: ['finagoTimeEventMappings'],
	queryFn: async () => (await finagoTimeEventMappingsGet()).data,
}

export const finagoTimeEventMappingsBatchPut = (dtos) =>
	serverPut('/api/v1/rest/calendar/time-tracking/mappings/batch', dtos)

export const eSalaryGetSalaryForPerson = (personNr) => serverGet(`/api/v1/rest/esalary/salary/${personNr}`)
export const eSalaryGetUsers = () => serverGet('/api/v1/rest/esalary/users')
export const eSalaryGetUsersQuery = {
	queryKey: ['eSalaryGetUsers'],
	queryFn: async () => {
		try {
			const { data } = await eSalaryGetUsers()
			return data ? normalizeUsers(data).filter((u) => u.role !== 'SYSTEM') : []
		} catch (e) {
			throw e.response.data.message
		}
	},
}
export const eSalaryUserPost = (user) => serverPost('/api/v1/rest/esalary/user', user)
export const eSalaryUpdateUser = (user) => serverPost('/api/v1/rest/esalary/updateuser', user)
//export const eSalarySendCredentials = (data) => serverPost("/api/v1/rest/esalary/send-credentials", data);
export const changePasswordPost = (request) => serverPost('/api/v1/rest/esalary/changepassword', request)
export const resetPasswordPost = (data) => serverPostPublic('/login/resetpassword', data, 'application/json')
export const resetPasswordChange = (data) => serverPostPublic('/login/resetpasswordchange', data, 'application/json')
export const eSalaryActivateHRUser = (data) => serverPost('/login/activatehruser', data, 'application/json')

export const organizationsImport = () => serverGet('/api/v1/rest/organizations/import')
export const organizationsGetAll = () => serverGet('/api/v1/rest/organizations/all')
export const organizationGet = () => serverGet('/api/v1/rest/organization')
export const organizationPost = (organization) => serverPost('/api/v1/rest/organization', organization)

export const organizationsQuery = {
	queryKey: ['organizations'],
	queryFn: async () => (await serverGet('/api/v1/rest/organizations/all')).data,
	staleTime: oneDayMs,
}

export const organizationDocumentsGetAll = (businessId) =>
	serverGet(`/api/v1/rest/organization/${businessId}/documents`)

export const organizationDocumentGet = (documentId) => serverGet(`/api/v1/rest/organization/document/${documentId}`)

export const organizationDocumentPost = (organizationId, documentId, organizationDocument) =>
	serverPost(`/api/v1/rest/organization/${organizationId}/document/${documentId}`, organizationDocument)

export const organizationDocumentDelete = (documentId) =>
	serverDelete(`/api/v1/rest/organization/document/${documentId}`)

export const organizationPropertyGet = (businessId, key) =>
	serverGet(`/api/v1/rest/organization/${businessId}/key/${key}`)

export const organizationPropertySet = (businessId, key, value) =>
	serverPost(`/api/v1/rest/organization/${businessId}/key/${key}`, value)

export const organizationPropertyDelete = (businessId, key) =>
	serverDelete(`/api/v1/rest/organization/${businessId}/key/${key}`)

export const organizationIntegrationsGet = (businessId) =>
	serverGet(`/api/v1/rest/organization/${businessId}/integrations`)

export const organizationGenerateHrToken = (businessId) => serverPost(`/api/v1/rest/organization/${businessId}/token`)

export const organizationTestToken = (integration) => serverGet(`/api/v1/rest/test-token/${integration}`)

export const finagoTimeCredentialsSave = (businessId, credentials) =>
	serverPost(`/api/v1/rest/organization/${businessId}/finago-time/credentials`, credentials)

export const organizationSettingsEsalaryGet = (businessId) =>
	serverGet(`/api/v1/rest/organization/${businessId}/settings/esalary`)

export const organizationSettingsEsalarySet = (businessId, settings) =>
	serverPost(`/api/v1/rest/organization/${businessId}/settings/esalary`, settings)

/** Get calendar settings for the authenticated organization */
export const organizationSettingsCalendarGet = () => serverGet(`/api/v1/rest/organization/settings/calendar`)

/** Update calendar settings for the authenticated organization */
export const organizationSettingsCalendarSet = (settings) =>
	serverPut(`/api/v1/rest/organization/settings/calendar`, settings)

export const personsSearch = (query) => serverPost('/api/v1/rest/persons/search', query)
export const personPost = (personData) => serverPost('/api/v1/rest/person', personData)
export const personPatch = (nr, personData) => serverPost(`/api/v1/rest/person/number/${nr}`, personData)
export const personGet = (personId) => serverGet(`/api/v1/rest/person/${personId}`)
export const personDelete = (personId) => serverDelete(`/api/v1/rest/person/${personId}`)
export const personNumberGet = () => serverGet('/api/v1/rest/person/number') // next available person number
export const personIdGet = (nr) => serverGet(`/api/v1/rest/person/id/number/${nr}`)
export const currentUserPersonIdGet = () => serverGet('/api/v1/rest/person/id/user')
export const currentUserPersonQuery = (enabled) => ({
	queryKey: ['currentUserPerson'],
	queryFn: async () => (await serverGet('/api/v1/rest/person/user')).data, // also Orval getCurrentUserPerson()
	enabled: !!enabled,
})

export const personNumberCheck = (nr) => serverGet(`/api/v1/rest/person/number/check/${nr}`)
export const personSocSecCheck = (socSec) => serverGet(`/api/v1/rest/person/socsec/check/${encodeURIComponent(socSec)}`)
export const procountorVersionCheck = (personId) =>
	serverGet(`/api/v1/rest/person/${personId}/procountor-version-check`)
export const personsGetAll = () => serverGet('/api/v1/rest/persons/all')
export const personsGetAllQuery = {
	queryKey: ['personsGetAll'],
	queryFn: () => personsGetAll(),
	staleTime: oneDayMs,
}
export const personsGetActive = () => serverGet('/api/v1/rest/persons/active')
export const personsGetActiveQuery = () => ({
	queryKey: ['personsGetActive'],
	queryFn: () => personsGetActive(),
	staleTime: oneDayMs,
})

export const personsGetSummary = (list) => serverGet(`/api/v1/rest/persons/summary/${list}`)
export const personsSummaryQuery = (list) => ({
	queryKey: ['personsSummary', list],
	queryFn: () => personsGetSummary(list),
	staleTime: oneDayMs,
})
export const personsSummaryMap = {
	queryKey: ['personsSummary', 'map'],
	queryFn: async () => {
		const summaryList = await personsGetSummary('all')
		const map = {}
		if (summaryList.data) {
			summaryList.data.forEach((person) => {
				map[person.personNumber] = person
			})
		}
		return map
	},
	staleTime: oneDayMs,
}

export const updatePersonAttachmentCount = (personJson) => serverPost('/api/v1/rest/person/attachmentcount', personJson)

export const personsGetAllModified = (modifiedSince) => serverGet(`/api/v1/rest/persons/all/modified/${modifiedSince}`)
export const importEmployeesPost = (request) => serverPost('/api/v1/rest/import-employees', request)
export const importCsv = (section, csv) =>
	serverPost(`/api/v1/rest/import-csv/${section}`, csv, 'text/csv;charset=utf-8')
export const exampleCsv = (section) => getFile(`/api/v1/rest/example-csv/${section}`)
export const importPsaPost = (update) => serverPost(`/api/v1/rest/import-psa?update=${update}`)
export const exportPsaPost = () => serverPost('/api/v1/rest/export-psa')
export const exportSeveraPost = (update, personIds) =>
	serverPost(`/api/v1/rest/export-severa?update=${update}`, personIds)

export const listProcountorQuery = (onlyActiveEmployees) => ({
	queryKey: ['listProcountor', onlyActiveEmployees],
	queryFn: async () =>
		(await serverGet(`/api/v1/rest/list-procountor?onlyActiveEmployees=${onlyActiveEmployees}`)).data,
})

export const importProcountorPost = (update, createNew, personIds) =>
	serverPost(`/api/v1/rest/import-procountor?update=${update}&createNew=${createNew}`, personIds)
export const exportProcountorPost = (update, createNew, personIds) =>
	serverPost(`/api/v1/rest/export-procountor?update=${update}&createNew=${createNew}`, personIds)

export const listNetvisorGet = () => serverGet('/api/v1/rest/list-netvisor')
export const importNetvisorPost = (update, personIds) =>
	serverPost(`/api/v1/rest/import-netvisor?update=${update}`, personIds)
export const exportNetvisorPost = (update, personIds) =>
	serverPost(`/api/v1/rest/export-netvisor?update=${update}`, personIds)

export const importDimensionsGet = () => serverGet('/api/v1/rest/import-dimensions')
export const checkEmployeesGet = () => serverGet('/api/v1/rest/check-employees')

export const contractsSearch = (query) => serverPost('/api/v1/rest/contracts/search', query)
export const contractPost = (contract) => serverPost('/api/v1/rest/contract', contract)
export const contractGet = (contractId) => serverGet(`/api/v1/rest/contract/${contractId}`)
export const contractsPersonGet = (personId) => serverGet(`/api/v1/rest/contracts/person/${personId}`)
export const contractDelete = (contractId) => serverDelete(`/api/v1/rest/contract/${contractId}`)
export const contractCountGet = (personNr) => serverGet(`/api/v1/rest/contracts/count/${personNr}`)
export const contractModelCountGet = () => serverGet('/api/v1/rest/contracts/model/count')
export const contractsGetAll = () => serverGet('/api/v1/rest/contracts/all')
export const contractsGetAllModified = (modifiedSince) =>
	serverGet(`/api/v1/rest/contracts/all/modified/${modifiedSince}`)
export const contractCompletePost = (contractId) => serverPost(`/api/v1/rest/contract/${contractId}/complete`)
export const contractReopenPost = (contractId) => serverPost(`/api/v1/rest/contract/${contractId}/reopen`)

export const codeListGet = (key) => serverGet(`/api/v1/rest/codelist/key/${key}`)

export const codeListGetQuery = (list) => ({
	queryKey: ['codeList', list],
	queryFn: () => codeListGet(list),
	staleTime: oneDayMs,
})

export const codeItemAdd = (key, item) => serverPost(`/api/v1/rest/codelist/key/${key}/item`, item)
export const codeListsGetAll = () => serverGet('/api/v1/rest/codelist/all')
export const codeListsGetAllModified = (modifiedSince) =>
	serverGet(`/api/v1/rest/codelist/all/modified/${modifiedSince}`)
export const codeItemsGetAllModified = (codeListId, modifiedSince) =>
	serverGet(`/api/v1/rest/codelist/${codeListId}/items/modified/${modifiedSince}`)

export const tagManagerPersist = (data) => serverPost('/api/v1/rest/codelist/tag-manager/tags/persist', data)
export const tagManagerKeys = (data) => serverPost('/api/v1/rest/codelist/tag-manager/tags/get/keys', data)
export const tagManagerIds = (data) => serverPost('/api/v1/rest/codelist/tag-manager/tags/get/ids', data)
export const tagManagerSearch = (keyPrefix, amount) =>
	serverGet('/api/v1/rest/codelist/tag-manager/tags/search', {
		keyPrefix: keyPrefix,
		amount: amount,
	})

export const calendarEventTypesQuery = {
	queryKey: ['calendarEventTypes'],
	queryFn: async () => {
		const res = await codeListGet('calendarEventTypes')
		const types = {}
		const {
			data: { items },
		} = res
		for (const item of items) {
			// categorize by memo field (Paid, Unpaid, Other)
			if (!types[item.memo]) types[item.memo] = []
			types[item.memo].push(item.codeKey)
		}
		return types
	},
	staleTime: oneDayMs,
}

export const educationGet = (id) => serverGet(`/api/v1/rest/education/${id}`)
export const educationDelete = (id) => serverDelete(`/api/v1/rest/education/${id}`)
export const educationPost = (data) => serverPost('/api/v1/rest/education', data)
export const educationsGetAll = (detailed) => serverGet(`/api/v1/rest/education/all${detailed ? '?detailed' : ''}`)
export const educationsGetAllModified = (modifiedSince) =>
	serverGet(`/api/v1/rest/education/all/modified/${modifiedSince}`)
export const educationsGetAllForPerson = (personId) => serverGet(`/api/v1/rest/education/all/person/${personId}`)
export const educationsGetAllForPersons = (detailed) =>
	serverGet(`/api/v1/rest/education/all/persons${detailed ? '?detailed' : ''}`)
export const educationsGetAllForPersonModified = (personId, modifiedSince) =>
	serverGet(`/api/v1/rest/education/all/person/${personId}/modified/${modifiedSince}`)

export const courseGet = (id) => serverGet(`/api/v1/rest/course/${id}`)
export const courseDelete = (id) => serverDelete(`/api/v1/rest/course/${id}`)
export const coursePost = (data) => serverPost('/api/v1/rest/course', data)
export const coursesGetAll = (detailed) => serverGet(`/api/v1/rest/course/all${detailed ? '?detailed' : ''}`)
export const coursesGetAllModified = (modifiedSince) => serverGet(`/api/v1/rest/course/all/modified/${modifiedSince}`)
export const coursesGetAllForPerson = (personId) => serverGet(`/api/v1/rest/course/all/person/${personId}`)
export const coursesGetAllForPersons = (detailed) =>
	serverGet(`/api/v1/rest/course/all/persons${detailed ? '?detailed' : ''}`)
export const coursesGetAllForPersonModified = (personId, modifiedSince) =>
	serverGet(`/api/v1/rest/course/all/person/${personId}/modified/${modifiedSince}`)

export const skillGet = (id) => serverGet(`/api/v1/rest/skill/${id}`)
export const skillDelete = (id) => serverDelete(`/api/v1/rest/skill/${id}`)
export const skillPost = (data) => serverPost('/api/v1/rest/skill', data)
export const skillsGetAll = (detailed) => serverGet(`/api/v1/rest/skill/all${detailed ? '?detailed' : ''}`)
export const skillsGetAllModified = (modifiedSince) => serverGet(`/api/v1/rest/skill/all/modified/${modifiedSince}`)
export const skillsGetAllForPerson = (personId) => serverGet(`/api/v1/rest/skill/all/person/${personId}`)
export const skillsGetAllForPersons = (detailed) =>
	serverGet(`/api/v1/rest/skill/all/persons${detailed ? '?detailed' : ''}`)
export const skillsGetAllForPersonModified = (personId, modifiedSince) =>
	serverGet(`/api/v1/rest/skill/all/person/${personId}/modified/${modifiedSince}`)

export const languageGet = (id) => serverGet(`/api/v1/rest/language/${id}`)
export const languageDelete = (id) => serverDelete(`/api/v1/rest/language/${id}`)
export const languagePost = (data) => serverPost('/api/v1/rest/language', data)
export const languagesGetAll = (detailed) => serverGet(`/api/v1/rest/language/all${detailed ? '?detailed' : ''}`)
export const languagesGetAllModified = (modifiedSince) =>
	serverGet(`/api/v1/rest/language/all/modified/${modifiedSince}`)
export const languagesGetAllForPerson = (personId) => serverGet(`/api/v1/rest/language/all/person/${personId}`)
export const languagesGetAllForPersons = (detailed) =>
	serverGet(`/api/v1/rest/language/all/persons${detailed ? '?detailed' : ''}`)
export const languagesGetAllForPersonModified = (personId, modifiedSince) =>
	serverGet(`/api/v1/rest/language/all/person/${personId}/modified/${modifiedSince}`)

export const discussionGet = (id) => serverGet(`/api/v1/rest/discussion/${id}`)
export const discussionDelete = (id) => serverDelete(`/api/v1/rest/discussion/${id}`)
export const discussionPost = (data) => serverPost('/api/v1/rest/discussion', data)
export const discussionsGetAll = () => serverGet('/api/v1/rest/discussions/all')
export const discussionsGetPerson = (personId) => serverGet(`/api/v1/rest/discussions/person/${personId}`)
export const discussionModelGet = (id) => serverGet(`/api/v1/rest/discussionmodel/${id}`)
export const discussionModelDelete = (id) => serverDelete(`/api/v1/rest/discussionmodel/${id}`)
export const discussionModelPost = (data) => serverPost('/api/v1/rest/discussionmodel', data)
export const discussionModelsGetAll = () => serverGet('/api/v1/rest/discussionmodels/all')
export const getTasksAndGoals = () => serverGet('/api/v1/rest/discussions/tasks-and-goals')
export const getTasksAndGoalsQuery = () => ({
	queryKey: ['tasksAndGoals'],
	queryFn: async () => (await getTasksAndGoals()).data,
	staleTime: oneDayMs,
})

export const equipmentGet = (id) => serverGet(`/api/v1/rest/equipment/${id}`)
export const equipmentDelete = (id) => serverDelete(`/api/v1/rest/equipment/${id}`)
export const equipmentPost = (data) => serverPost('/api/v1/rest/equipment', data)
export const equipmentsGetAll = (detailed) => serverGet(`/api/v1/rest/equipments/all${detailed ? '?detailed' : ''}`)
export const equipmentsQuery = {
	queryKey: ['equipments'],
	queryFn: equipmentsGetAll,
}
export const equipmentsGetPerson = (personId) => serverGet(`/api/v1/rest/equipments/person/${personId}`)
export const equipmentsGetPersonCars = (personId) => serverGet(`/api/v1/rest/equipments/person/${personId}/cars`)

export const employmentCertificateGet = (id) => serverGet(`/api/v1/rest/employment-certificate/${id}`)
export const employmentCertificateDelete = (id) => serverDelete(`/api/v1/rest/employment-certificate/${id}`)
export const employmentCertificatePost = (data) => serverPost('/api/v1/rest/employment-certificate', data)
export const employmentCertificatesGetAll = () => serverGet('/api/v1/rest/employment-certificates')
export const employmentCertificatesGetPerson = (personId) =>
	serverGet(`/api/v1/rest/employment-certificate/person/${personId}`)
export const prefillEmploymentCertificateGet = (personId) =>
	serverGet(`/api/v1/rest/employment-certificate/prefill/${personId}`)

export const statementTemplatesGet = async () => (await serverGet('/api/v1/rest/statement/reports/template/list')).data
export const statementTemplateGet = (id) => getFile(`/api/v1/rest/statement/reports/template/${id}`)
export const statementTemplatePost = async (name, xlsx) =>
	(await serverPost(`/api/v1/rest/statement/reports/template/create?name=${name}`, xlsx, "'multipart/form-data")).data

export const statementReportsGet = async () => (await serverGet('/api/v1/rest/statement/reports/list')).data
export const statementTrialTokensGet = async () => (await serverGet('/api/v1/rest/statement/trial-tokens')).data
export const statementTrialUsesGet = async () => (await serverGet('/api/v1/rest/statement/trial-uses')).data
export const statementReportPost = async (json) =>
	(await serverPost('/api/v1/rest/statement/reports/create', json)).data
export const statementReportXlsxGet = (id) => getFile(`/api/v1/rest/statement/reports/${id}/export`)
export const statementReportJsonGet = (id) => getFile(`/api/v1/rest/statement/reports/${id}/document`)
export const statementReportApprove = async (id) => await serverPost(`/api/v1/rest/statement/reports/${id}/approve`)
export const statementReportPatch = async (id, json) => await serverPatch(`/api/v1/rest/statement/reports/${id}`, json)
export const statementReportDelete = async (id) => await serverDelete(`/api/v1/rest/statement/reports/${id}`)

export const employmentHistoryGet = async () => (await serverGet('/api/v1/rest/employment-history')).data
export const employmentHistoryGetByPerson = async (personId) =>
	(await serverGet(`/api/v1/rest/persons/employment-history/${personId}`)).data
export const salaryHistoryGetByPerson = async (personId) =>
	(await serverGet(`/api/v1/rest/persons/salary-history/${personId}`)).data
export const salaryHistoryGetAll = () => serverGet('/api/v1/rest/persons/salary-history/all')

const personHierarchyGet = async () => (await serverGet('/api/v1/rest/hierarchy')).data
export const hierarchyQuery = {
	queryKey: ['hierarchy'],
	queryFn: personHierarchyGet,
}

export const calendarGetPersonAbsences = async (personId) => {
	if (!personId) return []

	const url = `/api/v1/rest/calendar/events/all?personId=${personId}`

	return (await serverGet(url)).data
}

const calendarGetAll = async () => (await serverGet('/api/v1/rest/calendar/events/all')).data
export const absencesByPersonQuery = (personId) => ({
	queryKey: ['absences', personId],
	queryFn: () => calendarGetPersonAbsences(personId),
	enabled: !!personId,
})
export const absencesQuery = {
	queryKey: ['absences'],
	queryFn: calendarGetAll,
}
export const workhoursQuery = {
	queryKey: ['workhours'],
	queryFn: calendarGetAll,
}
/**
 * Fetch calendar events for a date range with optional team filtering.
 * @param {boolean} [params.team] - If true, filters by supervisor relationships and masks sensitive data
 * @returns {Promise<Object>} Response object with calendar events data
 */
export const calendarGetRange = async (startDate, endDate, params = {}) => {
	const queryParams = new URLSearchParams()
	if (params.team) {
		queryParams.append('team', 'true')
	}
	const queryString = queryParams.toString()
	const url = `/api/v1/rest/calendar/events/from/${startDate}/to/${endDate}${queryString ? `?${queryString}` : ''}`

	return await serverGet(url)
}
export const calendarAddEvent = async (json) => (await serverPost('/api/v1/rest/calendar/event', json)).data
export const calendarDeleteEvent = async (eventId) => await serverDelete(`/api/v1/rest/calendar/event/${eventId}`)
export const calendarPatch = async (eventId, json) => await serverPatch(`/api/v1/rest/calendar/event/${eventId}`, json)
export const calendarSetStatus = async (status, eventIds) =>
	await serverPatch(`/api/v1/rest/calendar/status/${status}`, eventIds)
export const calendarImportSeveraAbsences = (json) => serverPost('/api/v1/rest/calendar/import/severa/absences', json)
export const calendarImportSeveraWorkhours = (json) => serverPost('/api/v1/rest/calendar/import/severa/workhours', json)

// LONG ABSENCES
export const getLongAbsenceEventTypes = async () => {
	return (await serverGet('/api/v1/rest/calendar/long-absence/event-types')).data
}
export const createLongAbsence = async (longAbsenceRequestDTO) => {
	return (await serverPost('/api/v1/rest/calendar/long-absence', longAbsenceRequestDTO)).data
}
export const longAbsenceTypesQuery = {
	queryKey: ['longAbsenceTypes'],
	queryFn: getLongAbsenceEventTypes,
	staleTime: Infinity,
}

export const notificationsGet = () => serverGet('/api/v1/rest/notifications')
export const notificationsGetPage = (page) => serverGet(`/api/v1/rest/notifications/page/${page}`)
export const notificationsGetBefore = (before, page) =>
	serverGet(`/api/v1/rest/notifications/before/${before}/page/${page}`)
export const notificationsGetAfter = (after, page) =>
	serverGet(`/api/v1/rest/notifications/after/${after}/page/${page}`)
export const notificationsGetBetween = (after, before, page) =>
	serverGet(`/api/v1/rest/notifications/after/${after}/before/${before}/page/${page}`)
export const notificationsRead = (notificationIds, read) =>
	serverPost(`/api/v1/rest/notifications/read/${read}`, notificationIds)
export const notificationRead = (notificationId, read) =>
	serverPost(`/api/v1/rest/notification/read/${notificationId}/${read}`)
export const notificationsReadAll = (read) => serverPost(`/api/v1/rest/notifications/all/read/${read}`)
export const notificationsGetUnread = () => serverGet('/api/v1/rest/notifications/unread')
export const remindersGet = () => serverGet('/api/v1/rest/reminders')
export const reminderRead = (notificationId, read) => serverPost(`/api/v1/rest/reminder/read/${notificationId}/${read}`)

export const activateProcountor = (requestId) => serverPostPublic(`/login/activate-procountor/${requestId}`)

export const localizationResourceGet = (language, namespace) =>
	localizationServerGet(`/api/v1/localization/${language}/${namespace}`)
export const localizationResourceAdd = (language, namespace, payload) =>
	localizationServerPost(`/api/v1/localization/${language}/${namespace}`, payload)

export const generatePdfReport = (reportId, reportJson) => pdfReportServerPost(`/api/report/${reportId}`, reportJson)
export const generateDocxReport = (reportId, reportJson) => pdfReportServerPost(`/api/docx/${reportId}`, reportJson)

export const storageSearch = (query) => storageServerPost('/api/search', query)
export const storageTags = (query) => storageServerGet('/api/tags', query)
export const storageMeta = (meta) => storageServerPatch(`/api/storage/${meta.key}`, meta)
export const storageGet = (meta) => storageServerGetFile(`/api/storage/${meta.key}`, meta)
export const storageDelete = (meta) => storageServerDelete(`/api/storage/${meta.key}`, meta)
export const storageUpload = (meta, data, progress) => storageServerPut('/api/storage', meta, data, progress)
export const storageUploadImage = (meta, data, progress) => storageServerPut('/api/image', meta, data, progress)

export const mappingsGet = () => serverGet('/api/v1/rest/calendar/mappings')
export const salaryTypesGet = () => serverGet('/api/v1/rest/procountor/salarytypes')
export const mappingBatchPut = (json) => serverPut('/api/v1/rest/calendar/mappings/batch', json)

export const addNewMapping = async (json) => (await serverPost('/api/v1/rest/calendar/mappings', json)).data

export const payTypesQuery = (integration, scope) => ({
	queryKey: ['payTypes', integration, scope, i18n.resolvedLanguage || 'fi'],
	staleTime: oneDayMs,

	queryFn: async () =>
		integration === 'PROCOUNTOR'
			? (
					await serverGet(
						`/api/v1/rest/procountor/salarytypes/options?language=${i18n.resolvedLanguage || 'fi'}&scope=${scope}`,
					)
				).data
			: (await eSalaryGet('absencepaytypelist')).data,

	select: (data) =>
		integration === 'PROCOUNTOR'
			? data.map((p) => ({
					// Procountor
					code: p.code,
					name: p.name || p.description || '',
					payrollSystemType: integration,
				}))
			: data.paytypes_response?.paytypelist?.map((p) => ({
					// eSalary
					code: p.code,
					name: p.name,
					payrollSystemType: integration,
				})),
})

export const mappingsQuery = {
	queryKey: ['mappings'],
	queryFn: async () => (await mappingsGet()).data,
	staleTime: oneDayMs,
}

export const salaryMappingsGet = () => serverGet('/api/v1/rest/salary-mapping/supplements')
export const salaryMappingBatchPut = (json) => serverPut('/api/v1/rest/salary-mapping/supplements/batch', json)

export const salaryMappingsSupplemmentsQuery = {
	queryKey: ['salaryMappings'],
	queryFn: async () => (await salaryMappingsGet()).data,
	staleTime: oneDayMs,
}

//Teams
export const teamsGetAll = () => serverGet('/api/v1/rest/teams')
export const teamGet = (id) => serverGet(`/api/v1/rest/teams/${id}`)
export const teamCreate = (team) => serverPost('/api/v1/rest/teams', team)
export const teamUpdate = (id, team) => serverPut(`/api/v1/rest/teams/${id}`, team)
export const teamDelete = (id) => serverDelete(`/api/v1/rest/teams/${id}`)
export const personTeamsUpdate = (personId, assignments) =>
	serverPut(`/api/v1/rest/persons/${personId}/teams`, assignments)
export const personsTeamsSummaryGet = () => serverGet('/api/v1/rest/persons/teams-summary')
export const personTeamsGet = (personId) => serverGet(`/api/v1/rest/persons/${personId}/teams-summary`)

//Groups
export const groupsGetAll = () => serverGet('/api/v1/rest/employee-groups')
export const groupsGetAllQuery = {
	queryKey: ['groupsGetAll'],
	queryFn: async () => (await groupsGetAll()).data,
	staleTime: oneDayMs,
}
export const groupGet = (id) => serverGet(`/api/v1/rest/employee-groups/${id}`)
export const groupCreate = (group) => serverPost('/api/v1/rest/employee-groups', group)
export const groupUpdate = (id, group) => serverPut(`/api/v1/rest/employee-groups/${id}`, group)
export const groupDelete = (id) => serverDelete(`/api/v1/rest/employee-groups/${id}`)
export const personGroupUpdate = (personId, assignment) =>
	serverPut(`/api/v1/rest/persons/${personId}/employee-group`, assignment)
export const personsGroupsSummaryGet = () => serverGet('/api/v1/rest/persons/employee-groups-summary')
export const personGroupGet = (personId) => serverGet(`/api/v1/rest/persons/${personId}/employee-group`)

//Vacations
export const vacationBalancesGetAll = () => serverGet('/api/v1/rest/vacation-balances')
export const vacationBalancesYearlyGetPerson = (personId) =>
	serverGet(`/api/v1/rest/vacation-balances-yearly/person/${personId}`)
